import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Calendar, MessageCircle, ArrowRight, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'

export const BaresCTA: React.FC = () => {
  const { t } = useTranslation('bares')

  return (
    <section className="relative py-32 bg-gradient-to-br from-amber-100 via-orange-100 to-yellow-50 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-72 h-72 bg-amber-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-yellow-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzk5OTk5OSIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>

      <div className="container relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-200/40 backdrop-blur-sm border border-amber-300/50 rounded-full text-amber-800 text-xs font-semibold mb-4">
              <Sparkles className="w-4 h-4" />
              {t('cta.kicker', 'Transforme seu negócio hoje')}
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 leading-tight">
              {t('cta.title')}
            </h2>

            <p className="text-sm text-gray-600 max-w-3xl mx-auto mb-8">
              {t('cta.description', 'Sistema completo de gestão para bares e restaurantes com pedido por QR, KDS integrado, conta por pessoa e ERP completo.')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          >
            <Link
              to="/contact?vertical=bares"
              className="group relative px-6 py-3 bg-amber-600 text-white font-semibold text-sm rounded-xl shadow-lg hover:bg-amber-700 hover:scale-105 transition-all duration-300 inline-flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              <span>{t('cta.buttons.demo')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link
              to="/contact?vertical=bares"
              className="group px-6 py-3 bg-white border-2 border-amber-600 text-amber-600 font-semibold text-sm rounded-xl hover:bg-amber-50 transition-all duration-300 inline-flex items-center gap-2 shadow-md"
            >
              <MessageCircle className="w-4 h-4" />
              <span>{t('cta.buttons.talk')}</span>
            </Link>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-12 flex flex-wrap gap-6 justify-center items-center text-gray-600"
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium">Setup em 48h</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium">Suporte dedicado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium">Sem contratos longos</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium">Treinamento incluído</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
